import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Home, HeartHandshake, HandCoins, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const SLIDES = [
    {
        icon: Home,
        title: "Swap your room",
        body: "Going on exchange? Someone from your exchange destination might be coming to your home city for the same period. Get matched, swap rooms.",
        gradient: "linear-gradient(135deg, #dd6b4d 0%, #e59374 50%, #f4c86b 100%)",
    },
    {
        icon: HeartHandshake,
        title: "Match with your Exchange Buddy",
        body: "No room to swap? Or just looking to make a connection? Get matched with an exchange buddy who is going to your city while you're going to theirs. Share (housing) tips, discover the best spots, and help each other feel at home.",
        gradient: "linear-gradient(135deg, #e59374 0%, #f4c86b 60%, #ffd894 100%)",
    },
    {
        icon: HandCoins,
        title: "Made by students, for students",
        body: "100% free. Just a community of exchange students helping each other move around the world.",
        gradient: "linear-gradient(135deg, #dd6b4d 0%, #b74e33 100%)",
    },
];

const KEY = "erasmusswap:onboarded";

export function markOnboarded() {
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
}

export function hasSeenOnboarding() {
    try { return localStorage.getItem(KEY) === "1"; } catch { return true; }
}

export default function Onboarding() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [idx, setIdx] = useState(0);
    const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
    const slide = SLIDES[idx];
    const isLast = idx === SLIDES.length - 1;

    // Lock body scroll so no vertical scrollbar can appear while onboarding is up.
    useEffect(() => {
        const prevBody = document.body.style.overflow;
        const prevHtml = document.documentElement.style.overflow;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevBody;
            document.documentElement.style.overflow = prevHtml;
        };
    }, []);

    const goNext = useCallback(() => {
        setDirection(1);
        setIdx((i) => Math.min(i + 1, SLIDES.length - 1));
    }, []);

    const goPrev = useCallback(() => {
        setDirection(-1);
        setIdx((i) => Math.max(i - 1, 0));
    }, []);

    const finish = (route) => { markOnboarded(); navigate(route); };

    // Where the primary CTA on the last slide should go — respects a logged-in
    // session so tapping the logo (which reopens onboarding) never forces a
    // sign-in again.
    const primaryRoute = user ? "/dashboard" : "/register";
    const primaryLabel = user ? "Continue to dashboard" : "Create free account";
    const skipRoute = user ? "/dashboard" : "/browse";

    const handleDragEnd = (_, info) => {
        const threshold = 60;
        const velocity = 400;
        if (info.offset.x < -threshold || info.velocity.x < -velocity) {
            if (!isLast) goNext();
        } else if (info.offset.x > threshold || info.velocity.x > velocity) {
            if (idx > 0) goPrev();
        }
    };

    const variants = {
        enter: (dir) => ({ opacity: 0, x: dir > 0 ? 60 : -60 }),
        center: { opacity: 1, x: 0 },
        exit: (dir) => ({ opacity: 0, x: dir > 0 ? -60 : 60 }),
    };

    return (
        <div
            data-testid="onboarding-page"
            className="fixed inset-0 z-[60] flex flex-col overflow-hidden bg-background touch-pan-y"
            style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
            {/* Skip */}
            <div className="flex justify-end px-6 pt-4">
                <button
                    data-testid="onboarding-skip"
                    onClick={() => finish(skipRoute)}
                    className="text-sm text-muted-foreground font-medium px-3 py-1.5 rounded-full hover:bg-muted"
                >
                    Skip
                </button>
            </div>

            {/* Slide content — swipe left/right to navigate, no vertical scroll */}
            <motion.div
                className="flex-1 flex items-center justify-center px-6 select-none"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.25}
                onDragEnd={handleDragEnd}
            >
                <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                        key={idx}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-sm w-full text-center pointer-events-none"
                    >
                        <div
                            className="w-40 h-40 mx-auto rounded-[48px] flex items-center justify-center shadow-2xl"
                            style={{ background: slide.gradient }}
                        >
                            <slide.icon className="w-20 h-20 text-white" strokeWidth={1.5} />
                        </div>
                        <h1 className="font-display text-3xl font-bold mt-10 tracking-tight leading-tight">
                            {slide.title}
                        </h1>
                        <p className="text-muted-foreground mt-4 text-base leading-relaxed">
                            {slide.body}
                        </p>
                    </motion.div>
                </AnimatePresence>
            </motion.div>

            {/* Dots + CTA */}
            <div className="px-6 pb-8" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 2rem)" }}>
                <div className="flex justify-center gap-2 mb-6" data-testid="onboarding-dots">
                    {SLIDES.map((_, i) => (
                        <button
                            key={i}
                            data-testid={`onboarding-dot-${i}`}
                            onClick={() => { setDirection(i > idx ? 1 : -1); setIdx(i); }}
                            aria-label={`Go to slide ${i + 1}`}
                            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-primary" : "w-1.5 bg-muted-foreground/30"}`}
                        />
                    ))}
                </div>

                {isLast ? (
                    <div className="space-y-3">
                        <Button
                            data-testid="onboarding-register"
                            onClick={() => finish(primaryRoute)}
                            className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-base font-semibold"
                        >
                            {primaryLabel}
                            <ArrowRight className="w-4 h-4 ml-1.5" />
                        </Button>
                        {!user && (
                            <Button
                                data-testid="onboarding-login"
                                onClick={() => finish("/login")}
                                variant="outline"
                                className="w-full h-12 rounded-full text-base font-semibold"
                            >
                                I already have an account
                            </Button>
                        )}
                        <button
                            data-testid="onboarding-continue-guest"
                            onClick={() => finish("/browse")}
                            className="w-full text-sm text-muted-foreground hover:text-foreground font-medium py-2 flex items-center justify-center gap-1"
                        >
                            <Sparkles className="w-3.5 h-3.5" />
                            {user ? "Browse rooms" : "Continue as guest"}
                        </button>
                    </div>
                ) : (
                    <Button
                        data-testid="onboarding-next"
                        onClick={goNext}
                        className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-base font-semibold"
                    >
                        Continue
                        <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                )}
            </div>
        </div>
    );
}
